import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { User } from '@interfaces/interface-bakend/seguridad/users/user.interface';
import { Parentesco, Nacionalidad, Sexo } from 'src/app/auth/inscripcion-os/interfaz-inscripcion-os/inscripcion-os-interface';
import { GecrosAfiliadosService } from 'src/app/services/entidades/gecros-afiliados.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UsuarioService } from 'src/app/services/shared/usuario.service';
import { SignoSedeService } from '@services/entidades/signo-sedes.service';
import { SignoPlanService } from '@services/entidades/signo-planes.service';
import { SignoPlan } from '@interfaces/interface-bakend/entidades/signo-plan.interface';
import { buscarPlanPorGecros } from '@shared/utils/plan.util';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-familiar-modal-alta',
  templateUrl: './familiar-modal-alta.component.html',
  styleUrls: ['./familiar-modal-alta.component.scss']
})
export class FamiliarModalAltaComponent implements OnInit {
  Parentesco = Parentesco;
  Nacionalidad = Nacionalidad;
  Sexo = Sexo;

  familiarForm: FormGroup;
  patronAlfabetico = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ'\s]{0,50}$/;
  patronNumerico = /^\d{0,10}$/;
  patronCuil = /^\d{0,12}$/;
  patronEmail = /^[^@]+@[^@]+\.[^@]+$/;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<FamiliarModalAltaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { titular: User },
    private gecrosAfiliadosService: GecrosAfiliadosService,
    private authService: AuthService,
    private usuarioService: UsuarioService,
    private signoSedeService: SignoSedeService,
    private signoPlanService: SignoPlanService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.familiarForm = this.fb.group({
      parentesco: [null, Validators.required],
      nacionalidad: [null, Validators.required],
      incapacidad: [null, Validators.required],
      nombre: ['', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
      apellido: ['', [Validators.required, Validators.pattern(this.patronAlfabetico)]],
      fechaNacimiento: [null, Validators.required],
      sexo: [null, Validators.required],
      cuil: ['', [Validators.required, Validators.pattern(this.patronCuil)]],
      numeroDocumento: [null, [Validators.required, Validators.pattern(this.patronNumerico)]],
      email: ['', [Validators.pattern(this.patronEmail)]]
    });

    this.familiarForm.get('parentesco')?.valueChanges.subscribe(() => this.validarHijoMayorQueTitular());
    this.familiarForm.get('fechaNacimiento')?.valueChanges.subscribe(() => this.validarHijoMayorQueTitular());
  }

  validarHijoMayorQueTitular(): void {
    const parentesco = this.familiarForm.get('parentesco')?.value;
    const fechaNacimiento = this.familiarForm.get('fechaNacimiento')?.value;
    const fechaTitular = this.data.titular?.birth ? new Date(this.data.titular.birth) : null;

    if (parentesco === Parentesco.Hijo && fechaNacimiento && fechaTitular) {
      const familiarDate = new Date(fechaNacimiento);
      if (familiarDate <= fechaTitular) {
        this.familiarForm.get('fechaNacimiento')?.setErrors({ hijoMayorQueTitular: true });
      } else {
        const errors = this.familiarForm.get('fechaNacimiento')?.errors;
        if (errors) {
          delete errors['hijoMayorQueTitular'];
          this.familiarForm.get('fechaNacimiento')?.setErrors(Object.keys(errors).length ? errors : null);
        }
      }
    }
  }

  esMayorDeEdad(): boolean {
    const fechaNacimiento = this.familiarForm.get('fechaNacimiento')?.value;
    if (!fechaNacimiento) return false;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad >= 18;
  }

  async guardar(): Promise<void> {
    if (!this.familiarForm.valid) {
      this.markFormGroupTouched(this.familiarForm);
      return;
    }

    const familiarData = this.familiarForm.value;
    const dni = familiarData.numeroDocumento.toString();

    // Mostrar loading mientras verificamos GECROS
    Swal.fire({
      title: 'Verificando en GECROS',
      text: 'Por favor espere...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      // 1. Verificar existencia en GECROS
      const resultadoGecros = await this.gecrosAfiliadosService.getAfiliadoByIdentificacion(dni);
      const existeEnGecros = resultadoGecros?.data?.length > 0 && resultadoGecros.data[0].documento !== undefined;

      if (!existeEnGecros) {
        Swal.fire({
          icon: 'error',
          title: 'Familiar no encontrado en GECROS',
          text: 'El familiar debe estar dado de alta en GECROS antes de poder agregarlo.',
        });
        return;
      }

      const datosGecros = resultadoGecros.data[0];
      Swal.close();

      // Mostrar confirmación formal antes de registrar
      const nombreCompleto = datosGecros.nombreAfiliado;
      const mensajeConfirmacion = `¿Está seguro de que desea registrar al familiar?<br><br>
        <strong>Nombre:</strong> ${nombreCompleto}<br>
        <strong>DNI:</strong> ${dni}<br>
        <strong>Validado en GECROS:</strong> Sí<br><br>
        Se procederá a crear el usuario en el sistema.`;

      const confirmacion = await Swal.fire({
        icon: 'question',
        title: 'Confirmar registro de familiar',
        html: mensajeConfirmacion,
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      });

      if (!confirmacion.isConfirmed) {
        return; // El usuario canceló
      }

      // Mostrar loading para registro
      Swal.fire({
        title: 'Registrando familiar',
        text: 'Creando usuario en el sistema...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      // Extraer código de sede (primeros 3 dígitos del numeroAfiliado)
      let codigoSedeFamiliar: string = null;
      let sedeFamiliar = null;
      if (datosGecros.numeroAfiliado && datosGecros.numeroAfiliado.length >= 3) {
        codigoSedeFamiliar = datosGecros.numeroAfiliado.substring(0, 3);
        sedeFamiliar = await this.signoSedeService.getSedeByCodigo(codigoSedeFamiliar);
      }

      // Obtener plan desde GECROS por denominación
      let planFamiliar = null;
      if (datosGecros.plan) {
        const planes = await this.signoPlanService.getPlanes();
        planFamiliar = (buscarPlanPorGecros(datosGecros.plan, planes) as SignoPlan) ?? null;
      }

      const esMayorEdad = this.esMayorDeEdad();
      const emailForm = familiarData.email;
      const emailValido = emailForm && this.esEmailValido(emailForm);
      const notifica = esMayorEdad && emailValido;

      let emailFinal: string;
      let emailConfirmFinal: string;

      if (esMayorEdad) {
        // Mayor de edad: usar email del formulario
        emailFinal = emailForm || '';
        emailConfirmFinal = emailForm || '';
      } else {
        // Menor de edad: generar correo arameo automático
        const nombre = datosGecros?.nombreAfiliado?.split(' ')[0] || familiarData.nombre;
        const apellido = datosGecros?.nombreAfiliado?.split(' ').slice(1).join(' ') || familiarData.apellido;
        const primeraLetraNombre = nombre.charAt(0).toLowerCase();
        const correoArameo = `${dni}@${primeraLetraNombre}${apellido.toLowerCase().replace(/\s+/g, '')}.ar`;
        emailFinal = correoArameo;
        emailConfirmFinal = correoArameo;
      }

      // Preparar datos de registro
      const datosRegistro: any = {
        email: emailFinal,
        emailConfirm: emailConfirmFinal,
        password: dni,
        passwordConfirm: dni,
        nombrePersona: datosGecros?.nombreAfiliado?.split(' ')[0] || familiarData.nombre,
        apellidoPersona: datosGecros?.nombreAfiliado?.split(' ').slice(1).join(' ') || familiarData.apellido,
        tipoIdentificacion: '6272eb83577d4b3218ec6ba6',
        identificacion: dni,
        aceptaTerminos: true,
        proteccionDeDatos: true,
        clausula: true,
        fromWeb: true,
        fromApp: false,
        signoGecrosAfiliado: datosGecros,
        signoPlan: planFamiliar?._id || null,
        codigoGecros: codigoSedeFamiliar,
        notifica: notifica,
        perfil: '65dccf7ac186b433577b3aef' as any,
        signoSedes: sedeFamiliar ? [sedeFamiliar._id] : [],
        birth: familiarData.fechaNacimiento,
        sexo: familiarData.sexo,
        estadoCivil: null,
        telefono: '',
        celular: ''
      };

      // Registrar usuario
      const registroExitoso = await this.authService.registerUser(datosRegistro);

      // Obtener usuario (ya sea recién creado o pre-existente)
      const usuarioFamiliar = await this.usuarioService.getFindOneByIdentification(dni);

      if (!registroExitoso && !usuarioFamiliar) {
        throw new Error('Error al registrar el familiar');
      }

      // Actualizar email_verified si notifica
      if (notifica) {
        await this.usuarioService.updateUser(usuarioFamiliar._id, { email_verified: true });
      }

      // Cerrar loading con éxito
      Swal.close();

      // Mostrar mensaje de éxito breve
      Swal.fire({
        icon: 'success',
        title: 'Familiar registrado',
        text: 'El familiar ha sido creado exitosamente.',
        timer: 1500,
        showConfirmButton: false
      });

      // Cerrar modal y devolver el objeto User completo para la relación
      this.dialogRef.close({
        success: true,
        familiar: usuarioFamiliar,
        parentesco: familiarData.parentesco
      });

    } catch (error) {
      console.error('Error al procesar familiar:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al registrar el familiar. Intente nuevamente.'
      });
    }
  }

  cancelar(): void {
    this.dialogRef.close();
  }

  private esEmailValido(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}